import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronUp, ChevronDown, ChevronsUpDown,
  PlayCircle, BookOpen, Clock, Eye, ChevronLeft, ChevronRight,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserDashboardLayout from '../../components/layout/UserDashboardLayout';
import { getAllWatchHistory, type AllWatchHistoryItem } from '../../api/packageService';
import { formatSeconds } from '../../utils/time';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const AdminWatchHistory: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AllWatchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseTypeFilter, setCourseTypeFilter] = useState<'All' | 'Course' | 'Webinar'>('All');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<keyof AllWatchHistoryItem>('lastWatchedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const storedUserData = (() => {
    try {
      const stored = localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  const isAdmin = !!(storedUserData?.isAdmin || storedUserData?.role === 'moderator' || storedUserData?.role === 'support_user');
  // Moderator/support_user keep the same sidebar (UserDashboardLayout) they
  // see everywhere else, so opening Watch History doesn't swap them into the
  // admin chrome — only real admins get the admin DashboardLayout here.
  const Layout = storedUserData?.isAdmin ? DashboardLayout : UserDashboardLayout;

  useEffect(() => {
    if (!isAdmin) navigate('/user-dashboard', { replace: true });
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      const res = await getAllWatchHistory(courseTypeFilter === 'All' ? undefined : courseTypeFilter);
      if (res.status === 200) setData(res.data || []);
      else setError(res.message);
      setLoading(false);
    };
    load();
  }, [courseTypeFilter, isAdmin]);

  useEffect(() => { setPage(1); }, [search, courseTypeFilter, sortField, sortDir, pageSize]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return data
      .filter(item =>
        !q ||
        item.userName.toLowerCase().includes(q) ||
        item.userEmail.toLowerCase().includes(q) ||
        item.userPhone.includes(q) ||
        item.courseTitle.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const av = a[sortField] ?? '';
        const bv = b[sortField] ?? '';
        const cmp = String(av) < String(bv) ? -1 : String(av) > String(bv) ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [data, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  if (!isAdmin) return null;

  const toggleSort = (field: keyof AllWatchHistoryItem) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const progressColor = (pct: number) =>
    pct >= 98 ? '#10b981' : pct >= 50 ? '#7c3aed' : '#f59e0b';

  const totalWatchSec = filtered.reduce((s, i) => s + i.totalWatchTime, 0);

  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const SortIcon = ({ field }: { field: keyof AllWatchHistoryItem }) => {
    if (sortField !== field) return <ChevronsUpDown size={13} style={{ opacity: 0.35 }} />;
    return sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
  };

  const statCards = [
    { label: 'Total Records',    value: filtered.length,                                         Icon: Eye,        color: '#7c3aed', bg: '#ede9fe' },
    { label: 'Total Watch Time', value: formatSeconds(totalWatchSec),                            Icon: Clock,      color: '#0891b2', bg: '#e0f2fe' },
    { label: 'Courses',          value: filtered.filter(i => i.courseType !== 'Webinar').length, Icon: BookOpen,   color: '#059669', bg: '#d1fae5' },
    { label: 'Webinars',         value: filtered.filter(i => i.courseType === 'Webinar').length, Icon: PlayCircle, color: '#d97706', bg: '#fef3c7' },
  ];

  const columns = [
    { label: '#',                field: null               },
    { label: 'Name',             field: 'userName'         },
    { label: 'Email',            field: 'userEmail'        },
    { label: 'Phone',            field: 'userPhone'        },
    { label: 'Course / Webinar', field: 'courseTitle'      },
    { label: 'Type',             field: 'courseType'       },
    { label: 'Progress',         field: 'progressPercent'  },
    { label: 'Watch Time',       field: 'totalWatchTime'   },
    { label: 'Started',          field: 'startedAt'        },
    { label: 'Last Watched',     field: 'lastWatchedAt'    },
  ] as const;

  return (
    <Layout>
      <div className="wh-page">

        {/* ── Gradient Header ── */}
        <div className="wh-header">
          <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: '12px', padding: '10px', display: 'flex' }}>
            <Eye size={24} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Watch History</h1>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', margin: '3px 0 0' }}>
              All course &amp; webinar watch activity across users
            </p>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="wh-stat-grid">
          {statCards.map(({ label, value, Icon, color, bg }) => (
            <div key={label} className="wh-stat-card">
              <div style={{ background: bg, borderRadius: '12px', padding: '10px', flexShrink: 0 }}>
                <Icon size={20} color={color} />
              </div>
              <div>
                <p style={{ fontSize: '10px', color: '#9ca3af', margin: 0, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {label}
                </p>
                <p style={{ fontSize: '22px', fontWeight: 800, color: '#111827', margin: '3px 0 0', lineHeight: 1 }}>
                  {value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters Bar ── */}
        <div className="wh-filters">
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: '200px', height: '40px' }}>
            <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search by name, email, phone or course…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', height: '100%', boxSizing: 'border-box',
                padding: '0 14px 0 36px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '13px',
                outline: 'none',
              }}
            />
          </div>

          {/* Type tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f9fafb', padding: '4px', borderRadius: '10px', border: '1px solid #e5e7eb', flexShrink: 0, height: '40px', boxSizing: 'border-box' }}>
            {(['All', 'Course', 'Webinar'] as const).map(t => (
              <button
                key={t}
                onClick={() => setCourseTypeFilter(t)}
                style={{
                  height: '100%',
                  padding: '0 14px',
                  borderRadius: '7px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  background: courseTypeFilter === t ? '#7c3aed' : 'transparent',
                  color: courseTypeFilter === t ? '#fff' : '#6b7280',
                  boxShadow: courseTypeFilter === t ? '0 2px 6px rgba(124,58,237,0.3)' : 'none',
                  transition: 'all 0.18s',
                  whiteSpace: 'nowrap',
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Rows per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, height: '40px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>Rows:</span>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              style={{
                height: '40px',
                padding: '0 10px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#374151',
                cursor: 'pointer',
                outline: 'none',
                background: '#fff',
                margin: '0px'
              }}
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* ── Table Card ── */}
        <div className="wh-card">
          {loading ? (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
              <div style={{
                width: '40px', height: '40px',
                border: '3px solid #e5e7eb', borderTop: '3px solid #7c3aed',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto',
              }} />
              <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '14px' }}>Loading watch history…</p>
            </div>
          ) : error ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <p style={{ color: '#ef4444', fontSize: '15px', fontWeight: 600 }}>{error}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <Eye size={44} color="#e5e7eb" style={{ margin: '0 auto 12px', display: 'block' }} />
              <p style={{ color: '#9ca3af', fontSize: '15px', fontWeight: 600, margin: 0 }}>No watch history found</p>
              <p style={{ color: '#d1d5db', fontSize: '13px', marginTop: '6px' }}>Try changing your search or filter</p>
            </div>
          ) : (
            <>
              {/* scrollable table wrapper — must NOT have overflow:hidden parent */}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', minWidth: '960px', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #f8f7ff 0%, #f0f4ff 100%)', borderBottom: '2px solid #ede9fe' }}>
                      {columns.map(({ label, field }) => (
                        <th
                          key={label}
                          onClick={() => field && toggleSort(field as keyof AllWatchHistoryItem)}
                          style={{
                            padding: '13px 16px',
                            textAlign: 'left',
                            fontWeight: 700,
                            color: '#5b21b6',
                            fontSize: '11px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.07em',
                            cursor: field ? 'pointer' : 'default',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {label}
                            {field && <SortIcon field={field as keyof AllWatchHistoryItem} />}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((item, idx) => (
                      <tr
                        key={`${item.userId}-${item.courseId}`}
                        style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#faf9ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = '')}
                      >
                        {/* # */}
                        <td style={{ padding: '14px 16px', color: '#9ca3af', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        {/* Name */}
                        <td style={{ padding: '14px 16px', fontWeight: 600, color: '#111827', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {item.userName || '—'}
                        </td>

                        {/* Email */}
                        <td style={{ padding: '14px 16px', color: '#4b5563', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {item.userEmail || '—'}
                        </td>

                        {/* Phone */}
                        <td style={{ padding: '14px 16px', color: '#4b5563', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {item.userPhone || '—'}
                        </td>

                        {/* Course title */}
                        <td style={{ padding: '14px 16px', minWidth: '180px', maxWidth: '240px' }}>
                          <p style={{ fontWeight: 500, color: '#1f2937', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.courseTitle}
                          </p>
                        </td>

                        {/* Type badge */}
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '3px 11px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                            background: item.courseType === 'Webinar' ? '#ede9fe' : '#dbeafe',
                            color: item.courseType === 'Webinar' ? '#6d28d9' : '#1d4ed8',
                          }}>
                            {item.courseType === 'Webinar' ? <PlayCircle size={11} /> : <BookOpen size={11} />}
                            {item.courseType === 'Webinar' ? 'Webinar' : 'Course'}
                          </span>
                        </td>

                        {/* Progress */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
                            <div style={{ flex: 1, height: '6px', background: '#f3f4f6', borderRadius: '999px', overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(item.progressPercent, 100)}%`,
                                height: '100%',
                                background: progressColor(item.progressPercent),
                                borderRadius: '999px',
                              }} />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: progressColor(item.progressPercent), minWidth: '34px', textAlign: 'right' }}>
                              {item.progressPercent.toFixed(0)}%
                            </span>
                          </div>
                        </td>

                        {/* Watch time */}
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            background: '#f0f4ff', color: '#4f46e5',
                            padding: '3px 10px', borderRadius: '8px',
                            fontSize: '12px', fontWeight: 600, fontFamily: 'monospace',
                          }}>
                            {formatSeconds(item.totalWatchTime)}
                          </span>
                        </td>

                        {/* Started */}
                        <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {formatDate(item.startedAt)}
                        </td>

                        {/* Last watched */}
                        <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {formatDate(item.lastWatchedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ── */}
              <div className="wh-pagination">
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                  Showing{' '}
                  <strong style={{ color: '#374151' }}>
                    {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)}
                  </strong>
                  {' '}of{' '}
                  <strong style={{ color: '#374151' }}>{filtered.length}</strong> results
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '7px 10px', borderRadius: '8px',
                      border: '1.5px solid #e5e7eb', background: '#fff',
                      color: page === 1 ? '#d1d5db' : '#374151',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <ChevronLeft size={15} />
                  </button>

                  {getPageNumbers().map((p, i) =>
                    p === '...' ? (
                      <span key={`e${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: '13px' }}>…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        style={{
                          width: '34px', height: '34px', borderRadius: '8px',
                          border: page === p ? 'none' : '1.5px solid #e5e7eb',
                          background: page === p ? '#7c3aed' : '#fff',
                          color: page === p ? '#fff' : '#374151',
                          cursor: 'pointer',
                          fontWeight: page === p ? 700 : 400,
                          fontSize: '13px',
                          boxShadow: page === p ? '0 2px 8px rgba(124,58,237,0.35)' : 'none',
                        }}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      padding: '7px 10px', borderRadius: '8px',
                      border: '1.5px solid #e5e7eb', background: '#fff',
                      color: page === totalPages ? '#d1d5db' : '#374151',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .wh-page {
          padding: 28px;
          max-width: 1400px;
          margin: 0 auto;
          box-sizing: border-box;
        }

        .wh-header {
          background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
          border-radius: 16px;
          padding: 24px 28px;
          margin-bottom: 24px;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .wh-stat-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        .wh-stat-card {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 14px;
          padding: 18px 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .wh-filters {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 14px;
          padding: 14px 18px;
          margin-bottom: 18px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.04);
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        /* key fix: border-radius + visible overflow so child can scroll */
        .wh-card {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 14px;
          overflow: visible;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }

        /* clip rounded corners only on top (header row) */
        .wh-card > div:first-child {
          border-radius: 14px 14px 0 0;
          overflow: hidden;
        }

        .wh-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          border-top: 1px solid #f3f4f6;
          flex-wrap: wrap;
          gap: 12px;
          background: #fafafa;
          border-radius: 0 0 14px 14px;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .wh-page { padding: 16px 12px; }
          .wh-header { padding: 18px 16px; border-radius: 12px; }
          .wh-header h1 { font-size: 18px !important; }
          .wh-stat-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 14px; }
          .wh-stat-card { padding: 14px; }
          .wh-filters { padding: 12px; gap: 10px; }
          .wh-pagination { padding: 12px 14px; }
        }

        @media (max-width: 480px) {
          .wh-stat-grid { grid-template-columns: repeat(2, 1fr); }
          .wh-stat-card { padding: 12px; gap: 10px; }
        }
      `}</style>
    </Layout>
  );
};

export default AdminWatchHistory;
